import React from 'react';
import { Zap, Lightbulb, Flame, ArrowRight, Truck, Wrench } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { equipmentData } from '../mock';

const Equipment = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="servicios" className="py-20 bg-gray-300 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <Zap className="w-12 h-12 text-orange-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Nuestros Servicios
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Equipos industriales de la más alta calidad para todos tus proyectos
          </p>
        </div>

        {/* Generators */}
        <div className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <Zap className="w-8 h-8 text-orange-500" />
            <h3 className="text-3xl font-bold text-gray-900">Generadores</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipmentData.generators.map((generator) => (
              <Card key={generator.id} className="bg-gray-100 border-gray-800 hover:border-orange-500 transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={generator.image} 
                    alt={generator.name}
                    className={`w-full h-full object-cover transition-transform duration-300 ${generator.id === 3 ? '' : 'group-hover:scale-110'}`}
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500 text-gray-900 border-0">
                      {generator.power}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-gray-900">{generator.name}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {generator.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {generator.features.map((feature, idx) => (
                      <Badge key={feature} variant="outline" className="border-orange-500/30 text-orange-500">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                  <button
                    onClick={scrollToContact}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-gray-900 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Cotizar</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Light Towers */}
        <div className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <Lightbulb className="w-8 h-8 text-orange-500" />
            <h3 className="text-3xl font-bold text-gray-900">Torres de Iluminación</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {equipmentData.lightTowers.map((tower) => (
              <Card key={tower.id} className="bg-gray-100 border-gray-800 hover:border-orange-500 transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={tower.image} 
                    alt={tower.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500 text-gray-900 border-0">
                      {tower.coverage}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-gray-900">{tower.name}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {tower.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tower.features.map((feature, idx) => (
                      <Badge key={feature} variant="outline" className="border-orange-500/30 text-orange-500">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                  <button
                    onClick={scrollToContact}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-gray-900 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Cotizar</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Welders */}
        <div className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <Flame className="w-8 h-8 text-orange-500" />
            <h3 className="text-3xl font-bold text-gray-900">Soldadoras</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {equipmentData.welders.map((welder) => (
              <Card key={welder.id} className="bg-gray-100 border-gray-800 hover:border-orange-500 transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={welder.image} 
                    alt={welder.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500 text-gray-900 border-0">
                      {welder.power}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-gray-900">{welder.name}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {welder.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {welder.features.map((feature, idx) => (
                      <Badge key={feature} variant="outline" className="border-orange-500/30 text-orange-500">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                  <button
                    onClick={scrollToContact}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-gray-900 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Cotizar</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Servicio Integral */}
        <div className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <Truck className="w-8 h-8 text-orange-500" />
            <h3 className="text-3xl font-bold text-gray-900">Servicio Integral</h3>
          </div>
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/30 hover:border-orange-500 transition-all duration-300 overflow-hidden group">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="relative h-80 lg:h-auto overflow-hidden">
                <img 
                  src={equipmentData.integralService.image} 
                  alt={equipmentData.integralService.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <h4 className="text-3xl font-bold text-gray-900 mb-4">
                  {equipmentData.integralService.name}
                </h4>
                <p className="text-xl text-gray-700 mb-4">
                  {equipmentData.integralService.description}
                </p>
                <p className="text-2xl font-bold text-orange-500 mb-6">
                  {equipmentData.integralService.tagline}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {equipmentData.integralService.features.map((feature, idx) => (
                    <Badge key={feature} variant="outline" className="border-orange-500/30 text-orange-500 text-sm py-2 justify-center">
                      {feature}
                    </Badge>
                  ))}
                </div>
                <button
                  onClick={scrollToContact}
                  className="bg-orange-500 hover:bg-orange-600 text-gray-900 py-3 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2 text-lg font-semibold"
                >
                  <span>Solicitar Servicio</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* Reparación y Mantenimiento */}
        <div>
          <div className="flex items-center space-x-3 mb-8">
            <Wrench className="w-8 h-8 text-orange-500" />
            <h3 className="text-3xl font-bold text-gray-900">Reparación y Mantenimiento</h3>
          </div>
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/30 hover:border-orange-500 transition-all duration-300 overflow-hidden">
            <div className="p-8">
              <div className="max-w-4xl mx-auto">
                <h4 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                  {equipmentData.repairService.name}
                </h4>
                <p className="text-xl text-gray-700 mb-4 text-center">
                  {equipmentData.repairService.description}
                </p>
                
                {/* Destacado especial */}
                <div className="bg-orange-500/20 border-2 border-orange-500 rounded-xl p-6 mb-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-orange-500 rounded-full p-3 flex-shrink-0">
                      <Wrench className="w-6 h-6 text-gray-900" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-500 mb-2">
                        {equipmentData.repairService.tagline}
                      </p>
                      <p className="text-lg text-gray-700">
                        Contamos con personal altamente especializado para garantizar la óptima operación de tu equipo. Mientras realizamos la reparación, te ofrecemos equipos en renta con tarifas preferenciales.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {equipmentData.repairService.features.map((feature, idx) => (
                    <div key={feature} className="bg-gray-100/50 border border-orange-500/30 rounded-lg p-4 text-center">
                      <p className="text-orange-500 font-semibold">{feature}</p>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={scrollToContact}
                    className="bg-orange-500 hover:bg-orange-600 text-gray-900 py-3 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2 text-lg font-semibold"
                  >
                    <Wrench className="w-5 h-5" />
                    <span>Solicitar Servicio de Reparación</span>
                  </button>
                  <a
                    href="https://wa.me/522292072733?text=Hola%2C%20necesito%20servicio%20de%20reparación%20y%20mantenimiento"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-gray-900 py-3 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2 text-lg font-semibold"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span>Consultar por WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Equipment;